/**
 * Search results list component - Apple HIG inspired design
 */
import { memo, useCallback } from 'react'
import { Stack, Text, Box, Center } from 'components'
import { IconSearch, IconFilter } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { ILvl4Item } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'
import { SearchResultCard } from './SearchResultCard'
import { SearchResultSkeleton } from './SearchResultSkeleton'
import { MIN_SEARCH_CHARS } from '../CurriculumTreeSearch/constants'

interface SearchResultsListProps {
  results: ILvl4Item[]
  totalResults: number
  isSearching: boolean
  showResults: boolean
  searchTermLength: number
  pendingIds: Set<string>
  alreadyAddedIds: Set<string>
  hasFilters: boolean
  onToggleItem: (item: ILvl4Item) => void
}

export const SearchResultsList = memo(({
  results, totalResults, isSearching, showResults,
  searchTermLength, pendingIds, alreadyAddedIds, hasFilters, onToggleItem
}: SearchResultsListProps) => {
  const { t } = useTranslation()

  const hasActiveSearch = searchTermLength >= MIN_SEARCH_CHARS
  const hasPendingItems = pendingIds.size > 0
  const showEmptyState = !hasActiveSearch && !hasPendingItems && !isSearching
  const showNoResults = hasActiveSearch && results.length === 0 && !hasPendingItems && !isSearching
  const hasResults = (results.length > 0 || hasPendingItems) && !isSearching

  const isSelected = useCallback((id: string) => pendingIds.has(id), [pendingIds])
  const isAdded = useCallback((id: string) => alreadyAddedIds.has(id), [alreadyAddedIds])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Results Header */}
      {hasResults && (
        <Box
          px={20}
          py={10}
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.colorScheme === 'dark'
              ? theme.colors.dark[4] : theme.colors.gray[2]}`,
            flexShrink: 0,
          })}
        >
          <Text size="xs" color="dimmed" weight={500}>
            {t('arc_results_count', { count: results.length })}
            {hasFilters && results.length !== totalResults && (
              <Text component="span" color="dimmed" weight={400}>
                {' '}{t('arc_results_filtered', { total: totalResults })}
              </Text>
            )}
          </Text>
        </Box>
      )}

      {/* Scrollable Results Area */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Box px={20} py={12}>
          {/* Empty State */}
          {showEmptyState && (
            <Center py={40}>
              <Stack align="center" spacing="sm">
                <Box
                  sx={(theme) => ({
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: theme.colorScheme === 'dark'
                      ? theme.colors.dark[5] : theme.colors.gray[1],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  })}
                >
                  <IconSearch size={24} color="gray" stroke={1.5} />
                </Box>
                <Stack spacing={2} align="center">
                  <Text size="sm" weight={500} color="dimmed">
                    {t('arc_search_empty_state')}
                  </Text>
                  <Text size="xs" color="dimmed" align="center">
                    {t('arc_search_empty_hint')}
                  </Text>
                </Stack>
              </Stack>
            </Center>
          )}

          {/* Loading State */}
          {isSearching && <SearchResultSkeleton />}

          {/* No Results State */}
          {showNoResults && (
            <Center py={40}>
              <Stack align="center" spacing="sm">
                <Box
                  sx={(theme) => ({
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: theme.colorScheme === 'dark'
                      ? theme.colors.dark[5] : theme.colors.gray[1],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  })}
                >
                  <IconFilter size={24} color="gray" stroke={1.5} />
                </Box>
                <Text size="sm" weight={500} color="dimmed">
                  {t('arc_no_results_found')}
                </Text>
              </Stack>
            </Center>
          )}

          {/* Results List */}
          {hasResults && (
            <Box sx={{ opacity: showResults ? 1 : 0, transition: 'opacity 0.2s ease-in-out' }}>
              <Stack spacing={10}>
                {results.map((item) => (
                  <SearchResultCard
                    key={item.id}
                    item={item}
                    isSelected={isSelected(item.id)}
                    isAlreadyAdded={isAdded(item.id)}
                    onToggle={onToggleItem}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
})

SearchResultsList.displayName = 'SearchResultsList'
